import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { ConfirmModal } from '../../components/common/Modal';
import { AppToast, type AppToastState } from '../../components/app-access/AppToast';
import { StateListEditor, type EditorState } from '../../components/domain/StateListEditor';
import {
  useCreateProjectTemplate,
  useProjectTemplate,
  useSoftDeleteProjectTemplate,
  useUpdateProjectTemplate,
} from '../../hooks/useProjectTemplates';
import { useProjects } from '../../hooks/useProjects';
import type { CreateProjectTemplateInput } from '../../services/api/projectTemplates';

/**
 * Admin template create + edit (Phase 20 UI-04, D-03..D-05).
 *
 * Single component for /admin/project-templates/new and
 * /admin/project-templates/:id (D-03 — `isEditing = !!useParams().id`).
 *
 * State list edits are buffered in local state and dispatched on Save
 * (D-03 — single PATCH per dirty save). Built-in Minimal renders the
 * editor in readOnly mode (D-04). Non-built-in edits show a soft-delete
 * button disabled when the template is the appliedTemplateId of any
 * project (TPL-06, D-05) — the disabled state is driven by a CLIENT-SIDE
 * count from useProjects() because the backend GET response schema does
 * not include inUseCount (fast-json-stringify strips unlisted fields).
 *
 * Phase 20 BL-01 fix: `ProjectListItem` (returned by useProjects()) now
 * exposes `appliedTemplateId` (added to `projectSummarySchema` and the
 * controller mapping in `backend/src/api/controllers/projectController.ts`).
 * The disabled-soft-delete tooltip therefore engages correctly — the
 * server-side 409 TPL-06 enforcement remains the authoritative gate.
 *
 * The description field omits the key when blank (NEVER sends null) —
 * the backend body schema (`backend/src/api/schemas/projectTemplate.ts`
 * line 73) is `{ type: 'string' }`, not nullable.
 */
export default function ProjectTemplateEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const templateId = isEditing ? parseInt(id!, 10) : undefined;

  const { data: detail, isLoading: detailLoading } = useProjectTemplate(templateId);

  // Client-side inUseCount for TPL-06 disabled-soft-delete tooltip (D-05).
  // useProjects() is already loaded in many other pages — no perf concern.
  // Phase 20 BL-01: `appliedTemplateId` is now a typed field on
  // ProjectListItem (backend schema + controller updated) so the filter
  // engages correctly. The server-side 409 remains the authoritative gate.
  const { data: allProjects } = useProjects();
  const inUseCount = useMemo(() => {
    if (!templateId || !allProjects) return 0;
    return allProjects.filter((p) => p.appliedTemplateId === templateId).length;
  }, [allProjects, templateId]);

  const createMutation = useCreateProjectTemplate();
  const updateMutation = useUpdateProjectTemplate();
  const softDeleteMutation = useSoftDeleteProjectTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [states, setStates] = useState<EditorState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<AppToastState | null>(null);

  // Hydrate local state when the detail arrives.
  useEffect(() => {
    if (detail && isEditing) {
      setName(detail.name);
      setDescription(detail.description ?? '');
      setStates(
        detail.states.map((s) => ({
          id: s.id,
          name: s.name,
          ordering: s.ordering,
          color: s.color,
        }))
      );
    }
  }, [detail, isEditing]);

  // WR-08: Server-side getProjectTemplates() filters out legacy templates,
  // but getProjectTemplate(:id) does NOT. A bookmarked or shared URL pointing
  // at a legacy template would render an editable form here, contradicting
  // Phase 17 D-04..D-06 (legacy templates must never reach the picker / editor).
  // Redirect to the list page when the loaded detail turns out to be legacy.
  useEffect(() => {
    if (detail?.isLegacy) {
      navigate('/admin/project-templates', { replace: true });
    }
  }, [detail?.isLegacy, navigate]);

  const isReadOnly = !!detail?.isBuiltIn; // D-04
  const canSoftDelete = isEditing && !isReadOnly && inUseCount === 0;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  function handleAdd(stateName: string, color: string) {
    setStates((prev) => [
      ...prev,
      {
        id: -Date.now() - prev.length,
        name: stateName,
        ordering: prev.length,
        color,
      },
    ]);
  }

  function handleRename(stateId: number, newName: string) {
    setStates((prev) => prev.map((s) => (s.id === stateId ? { ...s, name: newName } : s)));
  }

  function handleRecolor(stateId: number, newColor: string) {
    setStates((prev) => prev.map((s) => (s.id === stateId ? { ...s, color: newColor } : s)));
  }

  function handleDelete(stateId: number) {
    setStates((prev) =>
      prev
        .filter((s) => s.id !== stateId)
        .map((s, idx) => ({ ...s, ordering: idx }))
    );
  }

  function handleReorder(orderedIds: number[]) {
    setStates((prev) => {
      const byId = new Map(prev.map((s) => [s.id, s]));
      return orderedIds
        .map((stateId, idx) => {
          const s = byId.get(stateId);
          return s ? { ...s, ordering: idx } : null;
        })
        .filter((s): s is EditorState => s !== null);
    });
  }

  async function handleSave() {
    setError(null);
    if (!name.trim()) {
      setError('Template name is required.');
      return;
    }
    if (states.length === 0) {
      setError('At least one state is required.');
      return;
    }
    try {
      const statesPayload = states.map((s) => ({
        name: s.name,
        ordering: s.ordering,
        color: s.color,
      }));
      // Backend body schema rejects `description: null` — omit the key
      // entirely when blank. Conditional spread keeps the payload minimal.
      const trimmedDescription = description.trim();
      if (isEditing && templateId) {
        const patch: Partial<CreateProjectTemplateInput> = {
          name: name.trim(),
          states: statesPayload,
          ...(trimmedDescription ? { description: trimmedDescription } : {}),
        };
        await updateMutation.mutateAsync({ id: templateId, patch });
        setToast({ message: 'Template saved', variant: 'success' });
      } else {
        const payload: CreateProjectTemplateInput = {
          name: name.trim(),
          states: statesPayload,
          ...(trimmedDescription ? { description: trimmedDescription } : {}),
        };
        await createMutation.mutateAsync(payload);
        setToast({ message: 'Template created', variant: 'success' });
      }
      navigate('/admin/project-templates');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template.');
    }
  }

  async function handleConfirmedDelete() {
    if (!templateId) return;
    try {
      await softDeleteMutation.mutateAsync(templateId);
      setConfirmDelete(false);
      setToast({ message: 'Template deleted', variant: 'success' });
      navigate('/admin/project-templates');
    } catch (err) {
      setConfirmDelete(false);
      setToast({
        message: err instanceof Error ? err.message : 'Failed to delete template',
        variant: 'error',
      });
    }
  }

  if (isEditing && detailLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  const softDeleteTooltip =
    inUseCount > 0 ? `In use by ${inUseCount} project${inUseCount === 1 ? '' : 's'}` : '';

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <button
          onClick={() => navigate('/admin/project-templates')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Templates
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">
          {isEditing ? 'Edit Template' : 'New Template'}
        </h1>
        {isReadOnly && (
          <p className="mt-1 text-sm text-blue-700">
            This is the built-in Minimal template — it can be viewed but not edited or deleted.
          </p>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!isReadOnly) void handleSave();
        }}
        className="space-y-6"
      >
        <div>
          <label htmlFor="tpl-name" className="block text-sm font-medium text-gray-700">
            Name *
          </label>
          <input
            id="tpl-name"
            type="text"
            disabled={isReadOnly}
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
          />
        </div>

        <div>
          <label htmlFor="tpl-description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            id="tpl-description"
            rows={2}
            disabled={isReadOnly}
            value={description}
            // BL-02 / Phase 20: must match backend MAX_DESCRIPTION_LENGTH
            // (backend/src/api/schemas/projectTemplate.ts:17 = 500). Sending
            // 501-1000 chars surfaces a generic 400 only after Save.
            maxLength={500}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
          />
          {!isReadOnly && (
            <p className="mt-1 text-xs text-gray-500">{description.length}/500</p>
          )}
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-700 mb-2">States</h2>
          <StateListEditor
            states={states}
            mode="template"
            readOnly={isReadOnly}
            onAdd={handleAdd}
            onRename={handleRename}
            onRecolor={handleRecolor}
            onDelete={handleDelete}
            onReorder={handleReorder}
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {!isReadOnly && (
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button
              variant="secondary"
              onClick={() => navigate('/admin/project-templates')}
              type="button"
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={isSaving}>
              {isEditing ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        )}
      </form>

      {isEditing && !isReadOnly && (
        <div className="border-t border-gray-100 pt-6">
          <div className="flex items-center gap-3" title={softDeleteTooltip}>
            <Button
              variant="danger"
              size="sm"
              disabled={!canSoftDelete || softDeleteMutation.isPending}
              onClick={() => setConfirmDelete(true)}
            >
              Soft-delete template
            </Button>
            {softDeleteTooltip && (
              <span className="text-xs text-gray-500">{softDeleteTooltip}</span>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleConfirmedDelete}
        title="Soft-delete template"
        message={`Soft-delete "${detail?.name ?? ''}"? It will be hidden from the picker but historical projects keep their reference.`}
        confirmText="Soft-delete"
        variant="danger"
        isLoading={softDeleteMutation.isPending}
      />

      <AppToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
